import axios from "axios";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { validateResponse } from "@/api/validateResponse";
import { z } from "zod";
import {
  PartyAddonSchema,
  PartyAddonTypeSchema,
  PartyFormatSchema,
  PartyOccasionSchema,
  type PartyAddonType,
} from "@/api/types/party";
import { BookingStatusSchema } from "@/api/types/booking";

/**
 * Dedicated axios instance for the PUBLIC party portal — same rationale
 * as useMembershipPortal.ts's `membershipPortalClient`: a completely
 * separate, unauthenticated (token-in-query-param) backend surface
 * (app/api/portal_party.py, mounted at /api/public/portal/party, OUTSIDE
 * /api/v2). Never the staff `apiClient`, which redirects to /login on 401.
 */
const partyPortalClient = axios.create({
  baseURL: "/api/public/portal/party",
  headers: { "Content-Type": "application/json" },
});

const PartyPortalMeSchema = z.object({
  id: z.string(),
  resource_name: z.string(),
  booking_date: z.string(),
  status: BookingStatusSchema,
  occasion: PartyOccasionSchema,
  format: PartyFormatSchema,
  headcount: z.number().nullable(),
  base_price_cents: z.number(),
  addons: z.array(PartyAddonSchema),
  total_price_cents: z.number(),
  suggested_addons: z.array(PartyAddonTypeSchema),
});
export type PartyPortalMe = z.infer<typeof PartyPortalMeSchema>;

export const partyPortalKeys = {
  me: (token: string | null) => ["party-portal", "me", token] as const,
};

export function usePartyPortalMe(token: string | null) {
  return useQuery<PartyPortalMe>({
    queryKey: partyPortalKeys.me(token),
    queryFn: async () => {
      const { data } = await partyPortalClient.get("/me", { params: { token } });
      return validateResponse(PartyPortalMeSchema, data, "GET /public/portal/party/me");
    },
    enabled: !!token,
    retry: false,
    staleTime: 10 * 1000,
  });
}

export function useRequestPartyAddon(token: string | null) {
  const queryClient = useQueryClient();
  return useMutation<PartyPortalMe, unknown, { addon_type: PartyAddonType; quantity: number }>({
    mutationFn: async (input) => {
      const { data } = await partyPortalClient.post("/addons", input, { params: { token } });
      return validateResponse(PartyPortalMeSchema, data, "POST /public/portal/party/addons");
    },
    onSuccess: (me) => {
      queryClient.setQueryData(partyPortalKeys.me(token), me);
    },
  });
}
